import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow, type Locale } from "date-fns";
import { CheckCircle2, Clock, FileEdit, RefreshCw, XCircle, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { Job } from "@/lib/api/jobs";
import type { AuditLogEntry } from "@/lib/api/audit-logs";
import { dashboardRecentAuditLogsQueryOptions } from "../api";
import { jobsQueryOptions } from "@/features/jobs/api";
import { useLocale } from "@/hooks/use-locale";
import { ActivityRow, type ActivityRowVariant } from "./activity-row";
import { Button, FeedbackState, Segmented } from "@h2j-data-team/ui";

type FeedFilter = "all" | "jobs" | "sync" | "edits";

const SYNC_JOB_TYPES = new Set(["sync_app", "sync_group", "sync_all_groups"]);

type FeedItem = {
  id: string;
  kind: "job" | "audit";
  syncRelated: boolean;
  variant: ActivityRowVariant;
  icon: LucideIcon;
  title: string;
  meta?: string;
  duration?: string;
  timestamp: string;
  createdAt: number;
};

function jobToFeedItem(job: Job, jobTypeLabel: (type: string) => string, locale: Locale): FeedItem {
  const variant: ActivityRowVariant =
    job.status === "completed"
      ? "success"
      : job.status === "failed"
        ? "danger"
        : job.status === "processing" || job.status === "pending"
          ? "info"
          : "neutral";
  const icon =
    job.status === "completed"
      ? CheckCircle2
      : job.status === "failed"
        ? XCircle
        : job.status === "processing" || job.status === "pending"
          ? RefreshCw
          : Clock;
  const start = job.startedAt ? new Date(job.startedAt).getTime() : null;
  const end = job.completedAt ? new Date(job.completedAt).getTime() : null;
  const duration =
    start && end ? formatJobDuration(end - start) : job.status === "processing" ? "…" : undefined;
  const timestamp = formatDistanceToNow(new Date(job.createdAt), { addSuffix: true, locale });

  return {
    id: `job-${job.id}`,
    kind: "job",
    syncRelated: SYNC_JOB_TYPES.has(job.type),
    variant,
    icon,
    title: jobTypeLabel(job.type),
    meta: undefined,
    duration,
    timestamp,
    createdAt: new Date(job.createdAt).getTime(),
  };
}

function auditToFeedItem(entry: AuditLogEntry, t: TFunction, locale: Locale): FeedItem {
  const action = entry.action;
  const variant: ActivityRowVariant =
    action === "delete" ? "danger" : action === "create" ? "success" : "info";
  const icon = FileEdit;
  const entityLabel = t(`activity.entityTypes.${entry.entityType}`, {
    defaultValue: entry.entityType,
  });
  const actionLabel = t(`activity.actions.${entry.action}`, { defaultValue: entry.action });
  const summary = entry.payload?.summary ?? "";
  const actor = entry.actor?.name ?? t("activity.actor.system", { defaultValue: "System" });

  return {
    id: `audit-${entry.id}`,
    kind: "audit",
    syncRelated: false,
    variant,
    icon,
    title: `${actionLabel} · ${entityLabel}`,
    meta: summary ? `${summary} · ${actor}` : actor,
    duration: undefined,
    timestamp: formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true, locale }),
    createdAt: new Date(entry.createdAt).getTime(),
  };
}

function formatJobDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
}

const FILTERS: { id: FeedFilter; labelKey: string; defaultLabel: string }[] = [
  { id: "all", labelKey: "feed.filters.all", defaultLabel: "All" },
  { id: "jobs", labelKey: "feed.filters.jobs", defaultLabel: "Jobs" },
  { id: "sync", labelKey: "feed.filters.sync", defaultLabel: "Sync" },
  { id: "edits", labelKey: "feed.filters.edits", defaultLabel: "Edits" },
];

export function ActivityFeed({ jobTypeLabel }: { jobTypeLabel: (type: string) => string }) {
  const { t } = useTranslation("dashboard");
  const { dateFnsLocale } = useLocale();
  const [filter, setFilter] = useState<FeedFilter>("all");

  const jobsQuery = useQuery(jobsQueryOptions({ limit: 25 }));
  const auditQuery = useQuery(dashboardRecentAuditLogsQueryOptions);
  const { data: jobsResp, dataUpdatedAt: jobsUpdatedAt } = jobsQuery;
  const { data: auditResp, dataUpdatedAt: auditUpdatedAt } = auditQuery;

  const items = useMemo(() => {
    const merged = [
      ...(jobsResp?.data ?? []).map((job) => jobToFeedItem(job, jobTypeLabel, dateFnsLocale)),
      ...(auditResp?.data ?? []).map((entry) => auditToFeedItem(entry, t, dateFnsLocale)),
    ];
    return merged.sort((a, b) => b.createdAt - a.createdAt);
  }, [jobsResp, auditResp, jobTypeLabel, t, dateFnsLocale]);

  const filtered = items.filter((item) => {
    if (filter === "all") return true;
    if (filter === "jobs") return item.kind === "job";
    if (filter === "sync") return item.kind === "job" && item.syncRelated;
    if (filter === "edits") return item.kind === "audit";
    return true;
  });

  const visible = filtered.slice(0, 7);
  const lastUpdated = Math.max(jobsUpdatedAt ?? 0, auditUpdatedAt ?? 0);

  const segmentedOptions = FILTERS.map((f) => ({
    value: f.id,
    label: t(f.labelKey, { defaultValue: f.defaultLabel }),
  }));

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline pb-3">
        <div className="flex items-center gap-3">
          <h2 className="text-[14px] font-semibold text-ink">
            {t("feed.title", { defaultValue: "Activity" })}
          </h2>
          <Segmented
            options={segmentedOptions}
            value={filter}
            onValueChange={setFilter}
            size="compact"
            label={t("feed.filterLabel", { defaultValue: "Filter activity" })}
          />
        </div>
        {lastUpdated > 0 ? (
          <span className="text-[11px] text-faint">
            {t("feed.updated", {
              relative: formatDistanceToNow(new Date(lastUpdated), {
                addSuffix: true,
                locale: dateFnsLocale,
              }),
              defaultValue: `Updated ${formatDistanceToNow(new Date(lastUpdated), { addSuffix: true, locale: dateFnsLocale })}`,
            })}
          </span>
        ) : null}
      </div>

      {visible.length === 0 ? (
        <FeedFallback
          loading={jobsQuery.isPending || auditQuery.isPending}
          // Members get 403 on the audit log: like before, only the jobs feed can fail it.
          failed={jobsQuery.isError}
          onRetry={() => {
            void jobsQuery.refetch();
            void auditQuery.refetch();
          }}
        />
      ) : (
        <div className="flex flex-col">
          {visible.map((item) => (
            <ActivityRow
              key={item.id}
              icon={item.icon}
              variant={item.variant}
              title={item.title}
              meta={item.meta}
              duration={item.duration}
              timestamp={item.timestamp}
            />
          ))}
        </div>
      )}

      <Link
        to="/settings/audit"
        className="mt-2 self-center text-[12px] font-medium text-brand-text"
      >
        {t("feed.viewAll", { defaultValue: "View activity log →" })}
      </Link>
    </div>
  );
}

/** What the feed shows instead of rows: still loading, a failed request, or nothing to report. */
function FeedFallback({
  loading,
  failed,
  onRetry,
}: {
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation("dashboard");
  const { t: tCommon } = useTranslation("common");

  if (loading) {
    return <FeedbackState state="loading" title={tCommon("list.loading")} />;
  }
  if (failed) {
    return (
      <FeedbackState
        state="error"
        title={t("feed.loadFailed")}
        action={
          <Button variant="outline" size="sm" onClick={onRetry}>
            {tCommon("tryAgain")}
          </Button>
        }
      />
    );
  }
  return (
    <FeedbackState state="empty" title={t("feed.empty", { defaultValue: "No recent activity." })} />
  );
}
